import type { IntlShape } from 'react-intl'
import type { Dispatch } from 'react'

import { addToCheckoutCart, initBedbankSession, initialiseCheckout } from 'actions/CheckoutActions'
import { fetchPropertyRates } from 'actions/PropertyActions'
import { addToUserCart } from 'actions/UserCartActions'
import { trackEvent } from 'analytics/analytics'
import { addPropertyRatesToCartGTM } from 'analytics/eventDefinitions'
import { addPropertyRatesToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import { FlightViewTypes } from 'constants/flight'
import type { ModalContextType } from 'contexts/ModalContext'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import type { OfferPageDispatchAction, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import {
  generateBedbankPropertyAccommodationCheckoutItems,
  generateLpcPropertyAccommodationCheckoutItems,
} from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { floatify } from 'lib/maths/mathUtils'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isBedbankRate, isLpcRate } from 'lib/offer/propertyTypes'
import type { AppDispatch } from 'src/client/store'

import {
  createBundledCheckoutItem,
  validateBundledPropertyRateForCheckout,
} from './propertyBuyCallback'

interface PropertySelectedRateByRoom {
  room: OfferPageRoom
  rate: App.EnrichedPropertyRate
}

interface MultiRoomArgs {
  shouldAddToCart: boolean
  dispatch: AppDispatch
  currentCurrency: string
  airports?: Array<App.Airport>
  memberId?: string
  offerPageDispatch: Dispatch<OfferPageDispatchAction>
  modalElementContext: ModalElementContextValue<never>
  showModal: ModalContextType
  intl: IntlShape
  property: App.Property.Property
  selectedRatesByRoom: Array<PropertySelectedRateByRoom>
  originAirportCode: string | undefined
  flightQuotedPrice: number | undefined
  checkIn: string
  checkOut: string
  duration: number
  regionCode: string
  source: string
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
}

export async function propertyMultiRoomBookNowOrAddToCartCallback(args: MultiRoomArgs) {
  const {
    shouldAddToCart,
    dispatch,
    currentCurrency,
    airports,
    memberId,
    offerPageDispatch,
    modalElementContext,
    showModal,
    intl,
    property,
    selectedRatesByRoom,
    originAirportCode,
    flightQuotedPrice,
    checkIn,
    checkOut,
    duration,
    regionCode,
    source,
    isSpoofed,
    subscriber,
  } = args

  if (selectedRatesByRoom.length === 0) {
    return
  }

  const period = { checkIn, checkOut, duration }
  const accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem> = []
  const flightItems: Array<App.Checkout.FlightItem> = []
  const bundledExperienceItems: Array<App.Checkout.ExperienceItem | App.Checkout.TransferItem> = []
  const bundledItemIdsByRoomId: Record<string, Array<string>> = {}

  let closeProcessingModal: undefined | (() => void)
  const closeOpenProcessingModal = () => {
    closeProcessingModal?.()
    closeProcessingModal = undefined
  }

  const showProcessingModal = () => {
    if (closeProcessingModal) {
      return
    }
    showModal((resolve) => {
      closeProcessingModal = resolve
      return <ProcessingModal message={intl.formatMessage({
        id: '65JLQI9bUAc3Orze8tm-',
        defaultMessage: 'Please wait...',
        description: 'Processing modal message when initializing bedbank session',
      })}
      />
    })
  }

  if (selectedRatesByRoom.some(({ rate }) => !!rate.bundle) || (!shouldAddToCart && selectedRatesByRoom.some(({ rate }) => isBedbankRate(rate)))) {
    showProcessingModal()
  }

  // Refresh every occupancy room, not just the one that failed: a sold-out rate usually means our
  // rates are stale, and refreshing only the failing room leaves the others able to fail next.
  const refreshRatesAndShowSoldOut = () => {
    dispatch(fetchPropertyRates(property.id, {
      checkIn,
      checkOut,
      occupancies: selectedRatesByRoom.map(({ room }) => room),
      utmContent: undefined,
      roomTypeId: undefined,
      roomRateId: undefined,
      refresh: true,
    }))
    showModal(<BedbankSoldOutModal />)
  }

  for (const { room, rate } of selectedRatesByRoom) {
    if (!rate.bundle) continue
    const isValid = await validateBundledPropertyRateForCheckout({
      checkIn,
      checkOut,
      rooms: [room],
      regionCode,
      source,
      rate,
    })
    if (!isValid) {
      closeOpenProcessingModal()
      refreshRatesAndShowSoldOut()
      return
    }
  }

  for (const selection of selectedRatesByRoom) {
    const { room, rate } = selection
    const roomOccupancies = [room]

    if (isLpcRate(rate)) {
      const roomItems = generateLpcPropertyAccommodationCheckoutItems(rate, period, roomOccupancies)
      accommodationItems.push(...roomItems)
      bundledItemIdsByRoomId[room.id] = roomItems.map(({ itemId }) => itemId)
    } else if (isBedbankRate(rate)) {
      const bedbankRate: App.EnrichedBedbankPropertyRate = rate
      let sessionId: string | undefined
      if (!shouldAddToCart) {
        try {
          const cheapestFlightPrice = typeof bedbankRate.cheapestFlightPrice === 'number' ? bedbankRate.cheapestFlightPrice : 0
          const [hotelSellingPrice, hotelLuxPlusSellingPrice] = bedbankRate.bundle ? [
            bedbankRate.bundle.hotelSellingPrice,
            bedbankRate.bundle.hotelLuxPlusSellingPrice || undefined,
          ] : [
            floatify(bedbankRate.totals.sellingPrice - cheapestFlightPrice),
            bedbankRate.luxPlusTotals?.sellingPrice ? floatify(bedbankRate.luxPlusTotals.sellingPrice - cheapestFlightPrice) : undefined,
          ]
          if (!bedbankRate.service.bedGroups?.[0]) {
            closeOpenProcessingModal()
            refreshRatesAndShowSoldOut()
            return
          }
          const session = await initBedbankSession(dispatch, {
            propertyId: bedbankRate.service.propertyId,
            roomTypeId: bedbankRate.service.roomId,
            roomRateId: bedbankRate.service.rateId,
            rateGroupId: bedbankRate.service.rateGroupId,
            bedGroupId: bedbankRate.service.bedGroups[0].id,
            isFlightBundle: bedbankRate.isFlightBundle,
            csBooking: isSpoofed,
            region: regionCode,
            rooms: roomOccupancies,
            checkIn,
            checkOut,
            price: hotelSellingPrice,
            propertyFees: bedbankRate.totals.propertyFees,
            memberInclusive: hotelLuxPlusSellingPrice,
            subscriber,
            source,
          })

          if (!('sessionId' in session)) {
            closeOpenProcessingModal()
            refreshRatesAndShowSoldOut()
            return
          }

          sessionId = session.sessionId
        } catch {
          closeOpenProcessingModal()
          refreshRatesAndShowSoldOut()
          return
        }
      }

      const roomItems = generateBedbankPropertyAccommodationCheckoutItems(
        sessionId,
        bedbankRate,
        period,
        roomOccupancies,
        property.airportCode,
      )
      accommodationItems.push(...roomItems)
      bundledItemIdsByRoomId[room.id] = roomItems.map(({ itemId }) => itemId)
    }

    const bundle = rate.bundle
    if (bundle) {
      const accommodationItemIds = bundledItemIdsByRoomId[room.id] ?? []
      bundledExperienceItems.push(...await Promise.all(bundle.tickets.map(ticket =>
        createBundledCheckoutItem(accommodationItemIds, bundle.bundleId, ticket, checkIn, roomOccupancies, property, {
          currentCurrency,
          airports,
          currentRegionCode: regionCode,
          memberId,
        }),
      )))
    }
  }

  if (selectedRatesByRoom.some(({ rate }) => rate.includesFlights)) {
    if (!(originAirportCode && typeof flightQuotedPrice === 'number')) {
      closeOpenProcessingModal()
      throw new Error('Attempted to perform cart action on rate with flights when no flight pricing is available')
    }
    const rooms = selectedRatesByRoom.map(({ room }) => room)
    const flightOccupants = countOccupantsForFlights(rooms)
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode,
      destinationAirportCode: property.airportCode,
      occupants: flightOccupants,
      passengers: generatePassengersFromOccupants(flightOccupants),
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      bundledItemIds: accommodationItems.map(({ itemId }) => itemId),
      quotedFare: flightQuotedPrice,
    }))
  }

  closeOpenProcessingModal()

  const checkoutItems: Array<App.Checkout.AnyItem> = [...accommodationItems, ...flightItems, ...bundledExperienceItems]
  offerPageDispatch({ type: OfferPageActions.closeFilter })
  modalElementContext?.resolve()

  if (flightItems.length > 0) {
    dispatch(initialiseCheckout(checkoutItems, { mode: 'vertical-select' }))
  } else if (!shouldAddToCart) {
    dispatch(addToCheckoutCart(checkoutItems))
  }

  // Single conversion event with N item contexts; N separate events would over-attribute the booking.
  fireEcommerceGTMEvent(addPropertyRatesToCartGTM(
    property,
    selectedRatesByRoom.map(({ room, rate }) => ({ rate, quantity: sumUpOccupancies([room]) })),
  ))
  trackEvent(addPropertyRatesToCart(selectedRatesByRoom.map(({ rate }) => rate)))

  if (flightItems.length > 0) {
    dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    return
  }
  if (shouldAddToCart) {
    dispatch(addToUserCart(checkoutItems))
    return
  }
  dispatch(navigateToCheckout())
}
